/**
 * Fixedpoint-hjälpare. Alla positioner/avstånd i sim är heltal i 1/1000 world unit.
 * Endast heltalsaritmetik (och korrekt avrundad Math.sqrt på heltal, som är
 * deterministisk enligt IEEE 754) — samma resultat på alla plattformar.
 */

export const FP = 1000;

/** World units (flyttal, t.ex. från config) → fixedpoint. */
export function fpFromWorld(worldUnits: number): number {
  return Math.round(worldUnits * FP);
}

/** Fixedpoint → world units (endast för rendering/debug, aldrig spellogik). */
export function fpToWorld(fp: number): number {
  return fp / FP;
}

/** Heltalskvadratrot: största n där n*n <= v. Deterministisk. */
export function isqrt(v: number): number {
  if (v <= 0) return 0;
  let n = Math.floor(Math.sqrt(v));
  // Korrigera ev. flyttalsfel vid gränsen.
  while (n * n > v) n--;
  while ((n + 1) * (n + 1) <= v) n++;
  return n;
}

/** Avstånd i fixedpoint mellan två punkter. Säkert upp till ~2^26 fp per axel. */
export function fpDist(x0: number, y0: number, x1: number, y1: number): number {
  const dx = x1 - x0;
  const dy = y1 - y0;
  return isqrt(dx * dx + dy * dy);
}

/** Klampa v till [min, max]. */
export function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

/**
 * Flytta (x,y) mot (tx,ty) med steglängd stepFp. Når fram om avståndet är kortare.
 * Returnerar ny punkt som heltal.
 */
export function fpMoveToward(
  x: number,
  y: number,
  tx: number,
  ty: number,
  stepFp: number,
): { x: number; y: number } {
  const dx = tx - x;
  const dy = ty - y;
  const dist = isqrt(dx * dx + dy * dy);
  if (dist <= stepFp || dist === 0) return { x: tx, y: ty };
  return {
    x: x + Math.trunc((dx * stepFp) / dist),
    y: y + Math.trunc((dy * stepFp) / dist),
  };
}
